import { initializeApp } from 'firebase/app';
import {
  getAnalytics,
  isSupported,
  logEvent as firebaseLogEvent,
  setUserId as firebaseSetUserId,
  setUserProperties as firebaseSetUserProperties,
  type Analytics,
} from 'firebase/analytics';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

let analytics: Analytics | null = null;

if (firebaseConfig.apiKey) {
  const app = initializeApp(firebaseConfig);
  isSupported()
    .then((supported) => {
      if (supported) {
        analytics = getAnalytics(app);
      }
    })
    .catch(() => {
      // Analytics unsupported in this environment (e.g. private browsing); fail silently.
    });
} else {
  console.warn('Firebase config not found. Firebase Analytics is not initialized.');
}

const getEnvironment = () => {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' ? 'localhost' : 'production';
};

const isDebugModeEnabled = () => {
  try {
    if (new URLSearchParams(window.location.search).get('ga_debug') === 'true') {
      sessionStorage.setItem('ga_debug', 'true');
    }
    return sessionStorage.getItem('ga_debug') === 'true';
  } catch {
    return false;
  }
};

// Chrome on Android reduces the UA model to "K"; the real model is only available via Client Hints.
let hintedModel = '';
(
  navigator as Navigator & {
    userAgentData?: { getHighEntropyValues: (hints: string[]) => Promise<{ model?: string }> };
  }
).userAgentData
  ?.getHighEntropyValues(['model'])
  .then(({ model }) => {
    if (model) hintedModel = model;
  })
  .catch(() => {
    // Client Hints unavailable; fall back to the UA string.
  });

export const getDeviceModel = () => {
  if (hintedModel) return hintedModel;

  const ua = navigator.userAgent;

  const androidMatch = ua.match(/Android [\d.]+;\s*([^)]+?)\s*(?:Build\/|\))/);
  if (androidMatch) return androidMatch[1].trim();

  if (/iPad/.test(ua)) return 'iPad';
  if (/iPhone/.test(ua)) return 'iPhone';

  return 'unknown';
};

export const logEvent = (eventName: string, params?: Record<string, string | number | boolean>) => {
  if (analytics) {
    firebaseLogEvent(analytics, eventName, {
      ...params,
      environment: getEnvironment(),
      ...(isDebugModeEnabled() ? { debug_mode: true } : {}),
    });
  }
};

export const setAnalyticsUser = (user: { email: string; name: string } | null) => {
  if (!analytics) return;

  if (user) {
    firebaseSetUserId(analytics, user.email);
    firebaseSetUserProperties(analytics, { user_name: user.name, user_email: user.email });
  } else {
    firebaseSetUserId(analytics, null);
  }
};
